import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { I18N_LANGUAGE_KEY, I18N_MANUAL_SELECTION_KEY } from './constants/i18n';

// Custom language detector that checks backend API for geolocation-based language
const geoLanguageDetector = {
  name: 'geoLanguageDetector',
  
  lookup() {
    // Synchronous lookup - return undefined to let async detect handle it
    return undefined;
  },
  
  async: true,
  
  async detect(callback: (lng: string) => void) {
    try {
      // Check if user has a manual language selection flag
      const manualSelection = localStorage.getItem(I18N_MANUAL_SELECTION_KEY);
      const storedLang = localStorage.getItem(I18N_LANGUAGE_KEY);
      
      // Only respect stored language if it was manually selected by user
      if (manualSelection === 'true' && storedLang && (storedLang === 'es' || storedLang === 'en')) {
        console.log('Using manually selected language:', storedLang);
        callback(storedLang);
        return;
      }
      
      // Otherwise, always detect based on geolocation
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5051/api';
      const response = await fetch(`${apiUrl}/detect-language`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const detectedLang = data.language || 'en';
        console.log('Language detected via geolocation:', detectedLang, 'Country:', data.country);
        callback(detectedLang);
      } else {
        // Fallback to English for international users
        console.warn('Language detection API failed, defaulting to English');
        callback('en');
      }
    } catch (error) {
      console.warn('Failed to detect language from geolocation:', error);
      // Fallback to English for international users
      callback('en');
    }
  },
  
  cacheUserLanguage(lng: string) {
    localStorage.setItem(I18N_LANGUAGE_KEY, lng);
    // Mark that this was a manual selection by the user
    localStorage.setItem(I18N_MANUAL_SELECTION_KEY, 'true');
  }
};

// Translation resources
const resources = {
  es: {
    translation: {
      // App branding
      app: {
        name: 'ScoutMindAI',
        tagline: 'Plataforma integral para clubes deportivos',
        description: 'Plataforma integral con IA para la gestión completa de clubes deportivos. Optimiza entrenamientos, scouting, medicina deportiva y administración.'
      },

      // Landing Page - Get in Touch Section
      getInTouch: {
        sectionTitle: 'DETRÁS DE SCOUTMIND AI',
        name: 'Pedro Suárez Castaño',
        aboutMeTitle: 'Sobre mí',
        aboutMeText: 'Ingeniero Informático y Senior IT Project Manager con más de 13 años de experiencia en transformación digital y proyectos de tecnología deportiva.',
        aboutMeText2: 'Actualmente lidero ScoutMind para ofrecer soluciones a medida que optimizan la operativa y la toma de decisiones en organizaciones deportivas.',
        linkedinButton: 'Encuéntrame en LinkedIn',
        careerTitle: 'Mi extensa trayectoria en el sector tecnológico y mi amor por el deporte me llevaron a descubrir el mundo del Sport Tech, donde puedo compaginar mis dos grandes pasiones y ayudar a clubes y entidades deportivas a tener una gestión más eficiente.',
        careerText: 'Esa misma pasión es la que me ha llevado a escribir sobre análisis de datos deportivos en Hoops Data Basketball.',
        instagramButton: 'Échale un vistazo'
      },

      // Landing Page - How We Work Section
      landing: {
        howWeWork: {
          sectionTitle: 'CÓMO TRABAJO',
          subtitle: 'Proceso simple y transparente, adaptado a tu presupuesto',
          steps: {
            step1: {
              title: 'Llamada inicial',
              description: 'Llamada de 30 minutos paran conocernos y entender cuáles son tus retos y objetivos. Sin compromiso.'
            },
            step2: {
              title: 'Propuesta a medida',
              description: 'Propuesta detallada con alcance, plazos y presupuesto trasparente en un máximo de 48h.'
            },
            step3: {
              title: 'Desarrollo ágil',
              description: 'Desarrollo iterativo con actualizaciones semanales, recogida de feedback constante.'
            },
            step4: {
              title: 'Lanzamiento y soporte',
              description: 'Asistencia en la fase de lanzamiento como en el post-lanzamiento para garantizar el éxito del proyecto.'
            }
          }
        }
      },

      // Landing Page - About Section
      about: {
        brandName: 'ScoutMindAI',
        title: 'Gestión inteligente, juego inteligente',
        ctaButton: 'Empecemos tu proyecto',
        benefits: {
          saveTime: {
            title: 'Ahorra tiempo',
            description: 'Automatización de tareas repetitivas y optimización de procesos existentes para centrarte en la gestión del equipo.'
          },
          betterDecisions: {
            title: 'Toma mejores decisiones',
            description: 'Analiza todos los datos de tu club de forma global y toma decisiones informadas y estratégicas.'
          },
          tailored: {
            title: 'A tu medida',
            description: 'Desarrollo personalizado que se adapta perfectamente al flujo de trabajo y necesidades específicas de tu club.'
          }
        }
      },
      //
      clients: {
        ligaHypermotion_line1: "Club de la",
        ligaHypermotion_line2: "Liga Hypermotion"
      },

      // Landing Page - Contact Section
      contact: {
        sectionTitle: 'CONTACTO',
        title: 'Empecemos ya tu proyecto',
        call: 'Email, Llamada o Whatsapp',
        description: 'Estamos aquí para ayudarte. No dudes en ponerte en contacto con nosotros.',
        email: 'Email',
        emailAddress: 'info@scoutmindai.com',
        phone: 'Teléfono',
        phoneNumber: '+34 608 755 358',
        schedule: 'Lunes a Viernes, 9:00 - 18:00',
        ctaTitle: 'Trabajemos Juntos',
        ctaDescription: 'Ya sea que necesites una plataforma completa, herramientas de análisis de datos o integración de sistemas, estoy aquí para ayudar a tu club a alcanzar sus objetivos tecnológicos.',
        ctaButton: 'Contacta, ¡no lo dudes!'
      },

      // Landing Page - Footer Section
      footer: {
        tagline: 'Soluciones tecnológicas profesionales para deportes',
        phone: '+34 608 755 358',
        email: 'info@scoutmindai.com',
        copyright: '©2025 ScoutMind AI. Todos los derechos reservados.',
        links: {
          privacy: 'Política de Privacidad',
          terms: 'Términos y Condiciones',
          cookies: 'Política de Cookies'
        },
        social: {
          linkedin: 'LinkedIn',
          twitter: 'Twitter',
          github: 'GitHub'
        }
      },
      

      // Navigation
      nav: {
        dashboard: 'Panel Principal',
        agenda: 'Agenda',
        teams: 'Equipos',
        club: 'Club',
        training: 'Entrenamiento',
        sporting: 'Dirección Deportiva',
        videoAnalysis: 'Análisis de Video',
        medical: 'Área Médica',
        player: 'Jugador',
        messages: 'Mensajería',
        admin: 'Administración',
        settings: 'Configuración',
        logout: 'Cerrar Sesión'
      },
      
      // Authentication
      auth: {
        login: 'Iniciar Sesión',
        register: 'Registrarse',
        logout: 'Cerrar Sesión',
        username: 'Usuario',
        email: 'Correo Electrónico',
        password: 'Contraseña',
        confirmPassword: 'Confirmar Contraseña',
        firstName: 'Nombre',
        lastName: 'Apellidos',
        phone: 'Teléfono',
        club: 'Club',
        role: 'Rol',
        forgotPassword: '¿Olvidaste tu contraseña?',
        rememberMe: 'Recordarme',
        loginSuccess: 'Inicio de sesión exitoso',
        loginError: 'Error en el inicio de sesión',
        registerSuccess: 'Registro exitoso',
        registerError: 'Error en el registro',
        logoutSuccess: 'Sesión cerrada exitosamente',
        invalidCredentials: 'Credenciales inválidas',
        accountDeactivated: 'Cuenta desactivada',
        tokenExpired: 'Sesión expirada',
        passwordMismatch: 'Las contraseñas no coinciden',
        passwordTooShort: 'La contraseña debe tener al menos 6 caracteres',
        selectClub: 'Selecciona un club',
        selectRole: 'Selecciona un rol',
        createAccount: 'Crear cuenta',
        alreadyHaveAccount: '¿Ya tienes cuenta?',
        noAccount: '¿No tienes cuenta?',
        welcomeMessage: 'Accede a tu cuenta',
        enterCredentials: 'Ingresa tus credenciales para continuar'
      },
      
      // Common
      common: {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        view: 'Ver',
        add: 'Añadir',
        create: 'Crear',
        update: 'Actualizar',
        search: 'Buscar',
        filter: 'Filtrar',
        export: 'Exportar',
        import: 'Importar',
        loading: 'Cargando...',
        error: 'Error',
        success: 'Éxito',
        warning: 'Advertencia',
        info: 'Información',
        confirm: 'Confirmar',
        yes: 'Sí',
        no: 'No',
        back: 'Volver',
        next: 'Siguiente',
        previous: 'Anterior',
        close: 'Cerrar',
        open: 'Abrir',
        select: 'Seleccionar',
        clear: 'Limpiar',
        reset: 'Restablecer',
        submit: 'Enviar',
        download: 'Descargar',
        upload: 'Subir',
        print: 'Imprimir',
        share: 'Compartir',
        copy: 'Copiar',
        paste: 'Pegar',
        cut: 'Cortar',
        undo: 'Deshacer',
        redo: 'Rehacer',
        refresh: 'Actualizar',
        reload: 'Recargar',
        home: 'Inicio',
        dashboard: 'Panel',
        profile: 'Perfil',
        settings: 'Configuración',
        help: 'Ayuda',
        about: 'Acerca de',
        contact: 'Contacto',
        privacy: 'Privacidad',
        terms: 'Términos',
        language: 'Idioma',
        theme: 'Tema',
        notifications: 'Notificaciones',
        account: 'Cuenta',
        security: 'Seguridad',
        preferences: 'Preferencias',
        or: 'o',
        users: 'Usuarios',
        createdAt: 'Creado en',
        notSet: 'No establecido',
        total: 'Total',
        comingSoon: 'Próximamente',
        pageContent: 'contenido de la página',
        minutes: 'minutos',
        quickActions: 'Acciones Rápidas'
      },
      
      // Dashboard
      dashboard: {
        welcome: 'Bienvenido a ScoutMindAI',
        subtitle: 'Plataforma integral para la gestión de clubes deportivos',
        quickStats: 'Estadísticas Rápidas',
        recentActivity: 'Actividad Reciente',
        upcomingEvents: 'Próximos Eventos',
        notifications: 'Notificaciones',
        weather: 'Clima',
        news: 'Noticias',
        hello: 'Bienvenido a ',
        goodMorning: 'Buenos días',
        goodAfternoon: 'Buenas tardes',
        goodEvening: 'Buenas noches',
        noActivity: 'No hay actividad reciente',
        startActivity: 'Comienza creando contenido',
        exploreAreas: 'Explorar Áreas',
        exploreAreasSubtitle: 'Selecciona el área que quieres gestionar',
        areas: {
         club: {
            title: 'Área del Club',
            description: 'Gestiona la información y configuración de tu club'
          },
          training: {
            title: 'Área de Entrenamiento',
            description: 'Planifica y gestiona sesiones y planes de entrenamiento'
          },
          teams: {
            title: 'Área de Equipos',
            description: 'Administra equipos y sus jugadores'
          },
          sporting: {
            title: 'Área de Dirección Deportiva',
            description: 'Herramientas para scouting y análisis deportivo'
          },
          medical: {
            title: 'Área Médica',
            description: 'Gestión de salud y rendimiento físico de jugadores'
          },
          personal: {
            title: 'Área Personal',
            description: 'Accede a tu perfil y mensajes personales'
          }
        },
        searchTitle: '¿Qué jugador estás buscando?',
        searchDescription: 'Describe el tipo de jugador que necesitas en lenguaje natural. Puedo ayudarte a encontrar el perfil perfecto para tu equipo.',
        newConversation: 'Nueva conversación',
        searchAssistant: 'Asistente de búsqueda de jugadores',
        continuePlaceholder: 'Continúa la conversación o haz una nueva búsqueda...',
        searchPlaceholderInitial: 'Describe qué jugador buscas... ej: \'delantero joven de menos de 23 años\'',
        suggestion1: 'Busca delanteros jóvenes',
        suggestion2: 'Encuentra defensas altos',
        suggestion3: 'Jugadores menores de 20 años',
        suggestion4: 'Defensa central de menos de 25 años'
      },
      
      // Training Area
      training: {
        title: 'Área de Entrenamiento',
        sessions: 'Sesiones de Entrenamiento',
        session: 'Sesión',
        exercises: 'Ejercicios',
        exercise: 'Ejercicio',
        plans: 'Planes de Entrenamiento',
        plan: 'Plan',
        createSession: 'Crear Sesión',
        createExercise: 'Crear Ejercicio',
        createPlan: 'Crear Plan',
        editSession: 'Editar Sesión',
        editExercise: 'Editar Ejercicio',
        editPlan: 'Editar Plan',
        sessionDetails: 'Detalles de la Sesión',
        exerciseDetails: 'Detalles del Ejercicio',
        planDetails: 'Detalles del Plan',
        
        // Session fields
        sessionName: 'Nombre de la Sesión',
        sessionDate: 'Fecha',
        startTime: 'Hora de Inicio',
        duration: 'Duración (minutos)',
        description: 'Descripción',
        objectives: 'Objetivos',
        location: 'Ubicación',
        sessionType: 'Tipo de Sesión',
        intensityLevel: 'Nivel de Intensidad',
        weatherConditions: 'Condiciones Climáticas',
        attendanceCount: 'Asistencia',
        notes: 'Notas',
        isCompleted: 'Completada',
        
        // Exercise fields
        exerciseName: 'Nombre del Ejercicio',
        category: 'Categoría',
        subcategory: 'Subcategoría',
        equipmentNeeded: 'Material Necesario',
        playerCount: 'Número de Jugadores',
        spaceRequired: 'Espacio Requerido',
        instructions: 'Instrucciones',
        coachingPoints: 'Puntos de Entrenamiento',
        variations: 'Variaciones',
        orderInSession: 'Orden en la Sesión',
        
        // Plan fields
        planName: 'Nombre del Plan',
        planType: 'Tipo de Plan',
        startDate: 'Fecha de Inicio',
        endDate: 'Fecha de Fin',
        focusAreas: 'Áreas de Enfoque',
        
        // Session types
        sessionTypes: {
          entrenamiento: 'Entrenamiento',
          partido: 'Partido',
          recuperacion: 'Recuperación',
          tactica: 'Táctica',
          fisico: 'Físico',
          tecnico: 'Técnico'
        },
        
        // Intensity levels
        intensityLevels: {
          baja: 'Baja',
          media: 'Media',
          alta: 'Alta'
        },
        
        // Exercise categories
        exerciseCategories: {
          calentamiento: 'Calentamiento',
          tecnica: 'Técnica',
          tactica: 'Táctica',
          fisico: 'Físico',
          enfriamiento: 'Enfriamiento',
          juego_reducido: 'Juego Reducido',
          situaciones_especiales: 'Situaciones Especiales'
        },
        
        // Plan types
        planTypes: {
          temporada: 'Temporada',
          mesociclo: 'Mesociclo',
          microciclo: 'Microciclo',
          pretemporada: 'Pretemporada',
          competicion: 'Competición'
        },
        
        // Actions
        addExercise: 'Añadir Ejercicio',
        removeExercise: 'Eliminar Ejercicio',
        duplicateSession: 'Duplicar Sesión',
        exportSession: 'Exportar Sesión',
        printSession: 'Imprimir Sesión',
        markCompleted: 'Marcar como Completada',
        markIncomplete: 'Marcar como Incompleta',
        
        // Messages
        sessionCreated: 'Sesión creada exitosamente',
        sessionUpdated: 'Sesión actualizada exitosamente',
        sessionDeleted: 'Sesión eliminada exitosamente',
        exerciseAdded: 'Ejercicio añadido exitosamente',
        exerciseUpdated: 'Ejercicio actualizado exitosamente',
        exerciseDeleted: 'Ejercicio eliminado exitosamente',
        planCreated: 'Plan creado exitosamente',
        planUpdated: 'Plan actualizado exitosamente',
        planDeleted: 'Plan eliminado exitosamente',
        
        // Filters
        filterByTeam: 'Filtrar por Equipo',
        filterByType: 'Filtrar por Tipo',
        filterByDate: 'Filtrar por Fecha',
        filterByIntensity: 'Filtrar por Intensidad',
        allTeams: 'Todos los Equipos',
        allTypes: 'Todos los Tipos',
        allIntensities: 'Todas las Intensidades',
        
        // Statistics
        totalSessions: 'Total de Sesiones',
        completedSessions: 'Sesiones Completadas',
        averageDuration: 'Duración Promedio',
        totalExercises: 'Total de Ejercicios',
        mostUsedCategory: 'Categoría Más Usada',
        
        // Empty states
        noSessions: 'No hay sesiones de entrenamiento',
        noExercises: 'No hay ejercicios en esta sesión',
        noPlans: 'No hay planes de entrenamiento',
        createFirstSession: 'Crea tu primera sesión de entrenamiento',
        createFirstPlan: 'Crea tu primer plan de entrenamiento',
        completed: 'Completada',
        scheduled: 'Programada'
      },
      
      // Club Management
      club: {
        title: 'Gestión del Club',
        information: 'Información del Club',
        name: 'Nombre del Club',
        shortName: 'Nombre Corto',
        foundedYear: 'Año de Fundación',
        address: 'Dirección',
        phone: 'Teléfono',
        email: 'Correo Electrónico',
        website: 'Sitio Web',
        logo: 'Logo',
        sponsors: 'Patrocinadores',
        budget: 'Presupuesto',
        materials: 'Material',
        facilities: 'Instalaciones',
        communications: 'Comunicaciones',
        agreements: 'Convenios',
        statistics: 'Estadísticas',
        teamsCount: 'Número de Equipos',
        playersCount: 'Número de Jugadores',
        usersCount: 'Número de Usuarios',
        notFound: 'Club no encontrado',
        manage: 'Gestionar'
      },
      
      // Teams
      teams: {
        title: 'Equipos',
        team: 'Equipo',
        category: 'Categoría',
        ageGroup: 'Grupo de Edad',
        season: 'Temporada',
        players: 'Jugadores',
        coach: 'Entrenador',
        assistantCoach: 'Segundo Entrenador',
        physicalTrainer: 'Preparador Físico',
        analyst: 'Analista',
        categories: {
          primerEquipo: 'Primer Equipo',
          filial: 'Filial',
          cantera: 'Cantera',
          escuelaFutbol: 'Escuela de Fútbol',
          academiaFutbol: 'Academia de Fútbol',
          escuelaTecnificacion: 'Escuela de Tecnificación'
        },
        noTeams: 'No hay equipos',
        createFirstTeam: 'Crea tu primer equipo',
        manage: 'Gestionar Equipos'
      },
      
      // Players
      players: {
        title: 'Jugadores',
        player: 'Jugador',
        titleDashboardExecutive: 'Dashboard Ejecutivo',
        firstName: 'Nombre',
        lastName: 'Apellidos',
        dateOfBirth: 'Fecha de Nacimiento',
        placeOfBirth: 'Lugar de Nacimiento',
        age: 'Edad',
        nationality: 'Nacionalidad',
        position: 'Posición',
        jerseyNumber: 'Número de Camiseta',
        height: 'Altura',
        weight: 'Peso',
        dominantFoot: 'Pie Dominante',
        contractStart: 'Inicio de Contrato',
        contractEnd: 'Fin de Contrato',
        marketValue: 'Valor de Mercado',
        positions: {
          GK: 'Portero',
          DEF: 'Defensa',
          MID: 'Centrocampista',
          FWD: 'Delantero'
        },
        feet: {
          left: 'Izquierdo',
          right: 'Derecho',
          both: 'Ambos'
        },
        manage: 'Gestionar Jugadores',
        detail: 'Detalle del Jugador',
        additionalInfo: 'Información Adicional',
        matchesPlayed: 'Partidos jugados',
        goals: 'Goles',
        assists: 'Asistencias',
        yellowCards: 'Tarjetas amarillas',
        redCards: 'Tarjetas rojas',
        team: 'Equipo',
        league: 'Liga',
        notAvailable: 'No disponible',
        valor: 'Valor',
        noName: 'Sin nombre',
        noTeam: 'Sin equipo',
        basicInfo: 'Información Básica',
        basicStats: 'Estadísticas Básicas',
        expectedGoals: 'Goles Esperados (xG)',
        ballProgression: 'Progresión del Balón',
        realPerformance: 'Rendimiento por Partido (Real)',
        expectedPerformance: 'Rendimiento por Partido (Esperado)',
        competition: 'Competición',
        active: 'Activo',
        minutesPlayed: 'Minutos jugados',
        season: 'Temporada',
        extractionDate: 'Fecha extracción',
        expectedGoalsLabel: 'Goles Esperados',
        expectedGoalsNoPenalties: 'Goles Esperados (sin penalties)',
        expectedAssists: 'Asistencias Esperadas',
        expectedGAPlusA: 'G+A Esperadas (sin penalties)',
        withBall: 'Con Balón',
        withPasses: 'Con Pases',
        withDribbles: 'Con Regates',
        goalsPerMatch: 'Goles por Partido',
        assistsPerMatch: 'Asistencias por Partido',
        gaPlusAPerMatch: 'G+A por Partido',
        goalsNoPenaltiesPerMatch: 'Goles por Partido (sin penalties)',
        gaPlusANoPenaltiesPerMatch: 'G+A por Partido (sin penalties)',
        xgPerMatch: 'xG por Partido',
        xagPerMatch: 'xAG por Partido',
        xgPlusXagPerMatch: 'xG+xAG por Partido',
        npxgPerMatch: 'npxG por Partido',
        npxgPlusXagPerMatch: 'npxG+xAG por Partido',
        years: 'años',
        viewProfile: 'Ver perfil del jugador',
        agent: 'Agente',
        agentName: 'Nombre del Agente',
        socialMedia: 'Redes Sociales',
        loanExpiry: 'Fin de Cesión',

        enrichedInfo: 'Información Enriquecida',
        hasEnrichedInfo: 'Perfil Completo Disponible',
        enrichedData: 'Datos Enriquecidos',
        viewSocialMedia: 'Ver Redes Sociales',
        foot: 'Pie',
        contract: 'Contrato',
        assistsShort: 'Asist.',
        viewSocialProfile: 'Ver Perfil'
      },
      performanceHistoryTitle: 'Historial de Rendimiento',
      totalCompetitions: 'competición(es) totales',
      goals: 'Goles',
      assists: 'Asistencias',
      matches: 'Partidos',
      matches90: "Partidos (90')",
      season: 'Temporada',
      team: 'Equipo',
      playedMatches: 'PJ',
      goalsShort: 'G',
      assistsShort: 'A',
      minutes: 'Min',
      yellowCards: 'TA',
      redCards: 'TR',
      seeLess: 'Ver menos',
      seeAll: 'Ver todo',
      noPerformanceData: 'No hay datos de rendimiento por temporada disponibles para este jugador.',

      // Roles
      roles: {
        admin: 'Administrador',
        director_deportivo: 'Director Deportivo',
        entrenador: 'Entrenador',
        segundo_entrenador: 'Segundo Entrenador',
        preparador_fisico: 'Preparador Físico',
        analista: 'Analista',
        medico: 'Médico',
        fisio: 'Fisioterapeuta',
        preparador: 'Preparador',
        readaptador: 'Readaptador',
        nutricionista: 'Nutricionista',
        jugador: 'Jugador',
        secretaria_tecnica: 'Secretaría Técnica',
        scout: 'Scout'
      },
      
      // Errors
      errors: {
        generic: 'Ha ocurrido un error inesperado',
        network: 'Error de conexión',
        unauthorized: 'No autorizado',
        forbidden: 'Acceso denegado',
        notFound: 'No encontrado',
        validation: 'Error de validación',
        server: 'Error del servidor',
        timeout: 'Tiempo de espera agotado',
        offline: 'Sin conexión a internet'
      },
      
      // Sporting Direction
      sporting: {
        title: 'Dirección Deportiva',
        subtitle: 'Gestión estratégica y scouting',
        playerSearch: 'Buscador de Jugadores',
        playerSearchIA: 'Buscador IA de Jugadores',
        loanedPlayers: 'Seguimiento de Cedidos',
        reports: 'Informes de Jugadores',
        squadPlanning: 'Planificación de Plantillas',
        coachesDatabase: 'Base de Datos de Entrenadores',
        fieldMaps: 'Campogramas',
        expiringContracts: 'Contratos por Vencer',
        marketOpportunities: 'Oportunidades del Mercado',
        similarPlayers: 'Jugadores Similares',
        referees: 'Árbitros',
        searchPlaceholder: 'Busca jugadores usando lenguaje natural...',
        searchExample: 'Ej: "Busco un delantero joven de menos de 23 años que juegue en LaLiga"',
        noResults: 'No se encontraron jugadores',
        searching: 'Buscando jugadores...',
        comingSoon: 'Próximamente'
      },
      
      // Countries Explorer
      countries: {
        title: 'Explorador de Nacionalidades',
        subtitle: 'Descubre jugadores de todo el mundo organizados por país de nacimiento',
        backToCountries: 'Volver a países',
        totalPlayers: 'Total Jugadores',
        avgAge: 'Edad Promedio',
        withContract: 'Con Contrato',
        expiringContracts: 'Contratos por Vencer',
        topPositions: 'Posiciones Principales',
        topClubs: 'Clubes Principales',
        searchPlayer: 'Buscar jugador...',
        allPositions: 'Todas las posiciones',
        goalkeeper: 'Portero',
        defender: 'Defensa',
        midfielder: 'Mediocampo',
        forward: 'Delantero',
        sortByName: 'Nombre',
        sortByAge: 'Edad',
        loadingPlayers: 'Cargando jugadores...',
        noPlayersFound: 'No se encontraron jugadores',
        tryDifferentFilters: 'Intenta con otros filtros de búsqueda',
        previousPage: 'Anterior',
        nextPage: 'Siguiente',
        page: 'Página',
        of: 'de',
        registeredPlayers: 'jugadores registrados',
        loading: 'Cargando...',
        totalCountries: 'Total Países',
        averagePerCountry: 'Promedio por País',
        searchCountry: 'Buscar país...',
        allContinents: 'Todos los continentes',
        mostPlayers: 'Más jugadores',
        alphabetical: 'Alfabético',
        noCountriesFound: 'No se encontraron países',
        includeRetired: 'Incluir retirados',
        activePlayersOnly: 'Solo activos',
        clickToFilter: 'Click para filtrar'
      },
      
      // Video Analysis
      videoAnalysis: {
        title: 'Análisis de Video con IA',
        subtitle: 'Procesamiento automático de videos deportivos',
        upload: 'Subir Video',
        processing: 'Procesamiento',
        results: 'Resultados',
        uploadTab: 'Subir',
        processingTab: 'Procesamiento',
        resultsTab: 'Resultados',
        noAnalysisAvailable: 'No hay análisis disponibles',
        uploadVideoToStart: 'Sube un video para comenzar',
        downloadJson: 'Descargar JSON',
        downloadCsv: 'Descargar CSV',
        viewMetrics: 'Ver Métricas',
        viewSummary: 'Ver Resumen',
        deleteAnalysis: 'Eliminar Análisis',
        processingStatus: 'Procesando...',
        completed: 'Completado',
        failed: 'Falló',
        matchId: 'ID del Partido',
        videoPath: 'Ruta del Video'
      },
      
      // Success messages
      success: {
        saved: 'Guardado exitosamente',
        updated: 'Actualizado exitosamente',
        deleted: 'Eliminado exitosamente',
        created: 'Creado exitosamente',
        uploaded: 'Subido exitosamente',
        downloaded: 'Descargado exitosamente',
        sent: 'Enviado exitosamente',
        copied: 'Copiado al portapapeles',
        searchCompleted: 'Búsqueda completada exitosamente',
        playersFound: '{{count}} jugadores encontrados',
        registrationCompleted: 'Registro completado exitosamente'
      }
    }
  },
  en: {
    translation: {
      // App branding
      app: {
        name: 'ScoutMindAI',
        tagline: 'Comprehensive platform for football clubs',
        description: 'Comprehensive AI-powered platform for complete football club management. Optimize training, scouting, sports medicine and administration.'
      },

      // Landing Page - Get in Touch Section
      getInTouch: {
        sectionTitle: 'BEHIND SCOUTMIND AI',
        name: 'Pedro Suárez Castaño',
        aboutMeTitle: 'About me',
        aboutMeText: 'Computer Engineer and Senior IT Project Manager with over 13 years of experience in digital transformation and sports technology projects.',
        aboutMeText2: 'Currently leading ScoutMind to deliver tailored solutions that optimize operations and decision-making in sports organizations.',
        linkedinButton: 'Find me on LinkedIn',
        careerTitle: 'My extensive background in technology and my passion for sports led me to discover the world of Sport Tech, where I can combine my two great passions and help clubs and sports entities achieve more efficient management.',
        careerText: 'That same passion has led me to write about sports data analysis at Hoops Data Basketball.',
        instagramButton: 'Take a look'
      },

      // Landing Page - How We Work Section
      landing: {
        howWeWork: {
          sectionTitle: 'HOW I WORK',
          subtitle: 'Simple, transparent process adapted to your budget',
          steps: {
            step1: {
              title: 'Initial call',
              description: '30-minute call to get to know each other and understand your challenges and goals. No commitment required.'
            },
            step2: {
              title: 'Custom proposal',
              description: 'Detailed proposal with scope, timeline and transparent budget within 48 hours.'
            },
            step3: {
              title: 'Agile development',
              description: 'Iterative development with weekly updates, constant feedback collection.'
            },
            step4: {
              title: 'Launch and support',
              description: 'Assistance in the launch phase and post-launch to ensure the success of the project.'
            }
          }
        }
      },

      

      // Landing Page - About Section
      about: {
        brandName: 'ScoutMindAI',
        title: 'Smart management, smart play',
        call: 'Call, email or Whatsapp',
        ctaButton: 'Start your project',
        benefits: {
          saveTime: {
            title: 'Save time',
            description: 'Automation of repetitive tasks and optimization of existing processes to focus on team management.'
          },
          betterDecisions: {
            title: 'Make better decisions',
            description: 'Analyze all your club\'s data globally and make informed, strategic decisions.'
          },
          tailored: {
            title: 'Tailored to you',
            description: 'Personalized development that perfectly adapts to your club\'s workflow and specific needs.'
          }
        }
      },

      clients: {
        ligaHypermotion_line1: "Club of the",
        ligaHypermotion_line2: "Hypermotion League"
      },

      // Landing Page - Contact Section
      contact: {
        sectionTitle: 'CONTACT',
        title: 'Contact Information',
        call: 'Call me, Email or Whatsapp',
        description: 'We are here to help you. Feel free to get in touch with us.',
        email: 'Email',
        emailAddress: 'info@scoutmindai.com',
        phone: 'Phone',
        phoneNumber: '+34 608 755 358',
        schedule: 'Monday to Friday, 9:00 AM - 6:00 PM',
        ctaTitle: 'Let\'s Work Together',
        ctaDescription: 'Whether you need a complete platform, data analysis tools, or system integration, I\'m here to help your club achieve its technological goals.',
        ctaButton: 'Get in touch, don\'t hesitate!'
      },

      // Landing Page - Footer Section
      footer: {
        tagline: 'Professional sports technology solutions',
        phone: '+34 608 755 358',
        email: 'info@scoutmindai.com',
        copyright: '©2025 ScoutMind AI. All rights reserved.',
        links: {
          privacy: 'Privacy Policy',
          terms: 'Terms & Conditions',
          cookies: 'Cookie Policy'
        },
        social: {
          linkedin: 'LinkedIn',
          twitter: 'Twitter',
          github: 'GitHub'
        }
      },

      // Navigation
      nav: {
        dashboard: 'Dashboard',
        agenda: 'Agenda',
        teams: 'Teams',
        club: 'Club',
        training: 'Training',
        sporting: 'Sporting Direction',
        videoAnalysis: 'Video Analysis',
        medical: 'Medical Area',
        player: 'Player',
        messages: 'Messages',
        admin: 'Administration',
        settings: 'Settings',
        logout: 'Logout'
      },
      
      // Authentication
      auth: {
        login: 'Login',
        register: 'Register',
        logout: 'Logout',
        username: 'Username',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        firstName: 'First Name',
        lastName: 'Last Name',
        phone: 'Phone',
        club: 'Club',
        role: 'Role',
        forgotPassword: 'Forgot your password?',
        rememberMe: 'Remember me',
        loginSuccess: 'Login successful',
        loginError: 'Login error',
        registerSuccess: 'Registration successful',
        registerError: 'Registration error',
        logoutSuccess: 'Logout successful',
        invalidCredentials: 'Invalid credentials',
        accountDeactivated: 'Account deactivated',
        tokenExpired: 'Session expired',
        passwordMismatch: 'Passwords do not match',
        passwordTooShort: 'Password must be at least 6 characters',
        selectClub: 'Select a club',
        selectRole: 'Select a role',
        createAccount: 'Create account',
        alreadyHaveAccount: 'Already have an account?',
        noAccount: "Don't have an account?",
        welcomeMessage: 'Access your account',
        enterCredentials: 'Enter your credentials to continue'
      },
      
      // Common
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        view: 'View',
        add: 'Add',
        create: 'Create',
        update: 'Update',
        search: 'Search',
        filter: 'Filter',
        export: 'Export',
        import: 'Import',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        warning: 'Warning',
        info: 'Information',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        close: 'Close',
        open: 'Open',
        select: 'Select',
        clear: 'Clear',
        reset: 'Reset',
        submit: 'Submit',
        download: 'Download',
        upload: 'Upload',
        print: 'Print',
        share: 'Share',
        copy: 'Copy',
        paste: 'Paste',
        cut: 'Cut',
        undo: 'Undo',
        redo: 'Redo',
        refresh: 'Refresh',
        reload: 'Reload',
        home: 'Home',
        dashboard: 'Dashboard',
        profile: 'Profile',
        settings: 'Settings',
        help: 'Help',
        about: 'About',
        contact: 'Contact',
        privacy: 'Privacy',
        terms: 'Terms',
        language: 'Language',
        theme: 'Theme',
        notifications: 'Notifications',
        account: 'Account',
        security: 'Security',
        preferences: 'Preferences',
        or: 'or',
        users: 'Users',
        createdAt: 'Created at',
        notSet: 'Not set',
        total: 'Total',
        comingSoon: 'Coming Soon',
        pageContent: 'page content',
        minutes: 'minutes',
        quickActions: 'Quick Actions'
      },
      
      // Dashboard
      dashboard: {
        welcome: 'Welcome to ScoutMindAI',
        subtitle: 'Comprehensive platform for football club management',
        quickStats: 'Quick Stats',
        recentActivity: 'Recent Activity',
        upcomingEvents: 'Upcoming Events',
        notifications: 'Notifications',
        weather: 'Weather',
        news: 'News',
        hello: 'Welcome to ',
        goodMorning: 'Good morning',
        goodAfternoon: 'Good afternoon',
        goodEvening: 'Good evening',
        noActivity: 'No recent activity',
        startActivity: 'Start by creating content',
        exploreAreas: 'Explore Areas',
        exploreAreasSubtitle: 'Select the area you want to manage',
        areas: {
          club: {
            title: 'Club Area',
            description: 'Manage your club information and settings'
          },
          training: {
            title: 'Training Area',
            description: 'Plan and manage training sessions and plans'
          },
          teams: {
            title: 'Teams Area',
            description: 'Manage teams and their players'
          },
          sporting: {
            title: 'Sporting Direction Area',
            description: 'Tools for scouting and sports analysis'
          },
          medical: {
            title: 'Medical Area',
            description: 'Management of player health and physical performance'
          },
          personal: {
            title: 'Personal Area',
            description: 'Access your profile and personal messages'
          }
        },
        searchTitle: 'What player are you looking for?',
        searchDescription: 'Describe the type of player you need in natural language. I can help you find the perfect profile for your team.',
        newConversation: 'New conversation',
        searchAssistant: 'Player search assistant',
        continuePlaceholder: 'Continue the conversation or make a new search...',
        searchPlaceholderInitial: 'Describe what player you are looking for... e.g.: \'young striker under 23 years old\'',
        suggestion1: 'Search for young strikers',
        suggestion2: 'Find tall defenders',
        suggestion3: 'Players under 20 years old',
        suggestion4: 'Center back under 25 years old'
      },
      
      // Training Area
      training: {
        title: 'Training Area',
        sessions: 'Training Sessions',
        session: 'Session',
        exercises: 'Exercises',
        exercise: 'Exercise',
        plans: 'Training Plans',
        plan: 'Plan',
        createSession: 'Create Session',
        createExercise: 'Create Exercise',
        createPlan: 'Create Plan',
        editSession: 'Edit Session',
        editExercise: 'Edit Exercise',
        editPlan: 'Edit Plan',
        sessionDetails: 'Session Details',
        exerciseDetails: 'Exercise Details',
        planDetails: 'Plan Details',
        
        // Session fields
        sessionName: 'Session Name',
        sessionDate: 'Date',
        startTime: 'Start Time',
        duration: 'Duration (minutes)',
        description: 'Description',
        objectives: 'Objectives',
        location: 'Location',
        sessionType: 'Session Type',
        intensityLevel: 'Intensity Level',
        weatherConditions: 'Weather Conditions',
        attendanceCount: 'Attendance',
        notes: 'Notes',
        isCompleted: 'Completed',
        
        // Exercise fields
        exerciseName: 'Exercise Name',
        category: 'Category',
        subcategory: 'Subcategory',
        equipmentNeeded: 'Equipment Needed',
        playerCount: 'Number of Players',
        spaceRequired: 'Space Required',
        instructions: 'Instructions',
        coachingPoints: 'Coaching Points',
        variations: 'Variations',
        orderInSession: 'Order in Session',
        
        // Plan fields
        planName: 'Plan Name',
        planType: 'Plan Type',
        startDate: 'Start Date',
        endDate: 'End Date',
        focusAreas: 'Focus Areas',
        
        // Session types
        sessionTypes: {
          entrenamiento: 'Training',
          partido: 'Match',
          recuperacion: 'Recovery',
          tactica: 'Tactics',
          fisico: 'Physical',
          tecnico: 'Technical'
        },
        
        // Intensity levels
        intensityLevels: {
          baja: 'Low',
          media: 'Medium',
          alta: 'High'
        },
        
        // Exercise categories
        exerciseCategories: {
          calentamiento: 'Warm-up',
          tecnica: 'Technical',
          tactica: 'Tactical',
          fisico: 'Physical',
          enfriamiento: 'Cool-down',
          juego_reducido: 'Small-sided Game',
          situaciones_especiales: 'Set Pieces'
        },
        
        // Plan types
        planTypes: {
          temporada: 'Season',
          mesociclo: 'Mesocycle',
          microciclo: 'Microcycle',
          pretemporada: 'Pre-season',
          competicion: 'Competition'
        },
        
        // Actions
        addExercise: 'Add Exercise',
        removeExercise: 'Remove Exercise',
        duplicateSession: 'Duplicate Session',
        exportSession: 'Export Session',
        printSession: 'Print Session',
        markCompleted: 'Mark as Completed',
        markIncomplete: 'Mark as Incomplete',
        
        // Messages
        sessionCreated: 'Session created successfully',
        sessionUpdated: 'Session updated successfully',
        sessionDeleted: 'Session deleted successfully',
        exerciseAdded: 'Exercise added successfully',
        exerciseUpdated: 'Exercise updated successfully',
        exerciseDeleted: 'Exercise deleted successfully',
        planCreated: 'Plan created successfully',
        planUpdated: 'Plan updated successfully',
        planDeleted: 'Plan deleted successfully',
        
        // Filters
        filterByTeam: 'Filter by Team',
        filterByType: 'Filter by Type',
        filterByDate: 'Filter by Date',
        filterByIntensity: 'Filter by Intensity',
        allTeams: 'All Teams',
        allTypes: 'All Types',
        allIntensities: 'All Intensities',
        
        // Statistics
        totalSessions: 'Total Sessions',
        completedSessions: 'Completed Sessions',
        averageDuration: 'Average Duration',
        totalExercises: 'Total Exercises',
        mostUsedCategory: 'Most Used Category',
        
        // Empty states
        noSessions: 'No training sessions',
        noExercises: 'No exercises in this session',
        noPlans: 'No training plans',
        createFirstSession: 'Create your first training session',
        createFirstPlan: 'Create your first training plan',
        completed: 'Completed',
        scheduled: 'Scheduled'
      },
      
      // Club Management
      club: {
        title: 'Club Management',
        information: 'Club Information',
        name: 'Club Name',
        shortName: 'Short Name',
        foundedYear: 'Founded Year',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        website: 'Website',
        logo: 'Logo',
        sponsors: 'Sponsors',
        budget: 'Budget',
        materials: 'Materials',
        facilities: 'Facilities',
        communications: 'Communications',
        agreements: 'Agreements',
        statistics: 'Statistics',
        teamsCount: 'Number of Teams',
        playersCount: 'Number of Players',
        usersCount: 'Number of Users',
        notFound: 'Club not found',
        manage: 'Manage'
      },
      
      // Teams
      teams: {
        title: 'Teams',
        team: 'Team',
        category: 'Category',
        ageGroup: 'Age Group',
        season: 'Season',
        players: 'Players',
        coach: 'Coach',
        assistantCoach: 'Assistant Coach',
        physicalTrainer: 'Physical Trainer',
        analyst: 'Analyst',
        categories: {
          primerEquipo: 'First Team',
          filial: 'Reserve Team',
          cantera: 'Youth Academy',
          escuelaFutbol: 'Football School',
          academiaFutbol: 'Football Academy',
          escuelaTecnificacion: 'Technical School'
        },
        noTeams: 'No teams',
        createFirstTeam: 'Create your first team',
        manage: 'Manage Teams'
      },
      
      // Players
      players: {
        title: 'Players',
        player: 'Player',
        titleDashboardExecutive: 'Dashboard Executive',
        firstName: 'First Name',
        lastName: 'Last Name',
        dateOfBirth: 'Date of Birth',
        placeOfBirth: 'Place of Birth',
        age: 'Age',
        nationality: 'Nationality',
        position: 'Position',
        jerseyNumber: 'Jersey Number',
        height: 'Height',
        weight: 'Weight',
        dominantFoot: 'Dominant Foot',
        contractStart: 'Contract Start',
        contractEnd: 'Contract End',
        marketValue: 'Market Value',
        positions: {
          GK: 'Goalkeeper',
          DEF: 'Defender',
          MID: 'Midfielder',
          FWD: 'Forward'
        },
        feet: {
          left: 'Left',
          right: 'Right',
          both: 'Both'
        },
        manage: 'Manage Players',
        detail: 'Player Detail',
        additionalInfo: 'Additional Information',
        matchesPlayed: 'Matches played',
        goals: 'Goals',
        assists: 'Assists',
        yellowCards: 'Yellow cards',
        redCards: 'Red cards',
        team: 'Team',
        league: 'League',
        notAvailable: 'Not available',
        valor: 'Value',
        noName: 'No name',
        noTeam: 'No team',
        basicInfo: 'Basic Information',
        basicStats: 'Basic Statistics',
        expectedGoals: 'Expected Goals (xG)',
        ballProgression: 'Ball Progression',
        realPerformance: 'Real Performance per Match',
        expectedPerformance: 'Expected Performance per Match',
        competition: 'Competition',
        active: 'Active',
        minutesPlayed: 'Minutes played',
        season: 'Season',
        extractionDate: 'Extraction date',
        expectedGoalsLabel: 'Expected Goals',
        expectedGoalsNoPenalties: 'Expected Goals (no penalties)',
        expectedAssists: 'Expected Assists',
        expectedGAPlusA: 'G+A Expected (no penalties)',
        withBall: 'With Ball',
        withPasses: 'With Passes',
        withDribbles: 'With Dribbles',
        goalsPerMatch: 'Goals per Match',
        assistsPerMatch: 'Assists per Match',
        gaPlusAPerMatch: 'G+A per Match',
        goalsNoPenaltiesPerMatch: 'Goals per Match (no penalties)',
        gaPlusANoPenaltiesPerMatch: 'G+A per Match (no penalties)',
        xgPerMatch: 'xG per Match',
        xagPerMatch: 'xAG per Match',
        xgPlusXagPerMatch: 'xG+xAG per Match',
        npxgPerMatch: 'npxG per Match',
        npxgPlusXagPerMatch: 'npxG+xAG per Match',
        years: 'years',
        viewProfile: 'View player profile',
        agent: 'Agent',
        agentName: 'Agent Name',
        socialMedia: 'Social Media',
        loanExpiry: 'Loan Expiry',
        enrichedInfo: 'Enriched Information',
        hasEnrichedInfo: 'Full Profile Available',
        enrichedData: 'Enriched Data',
        viewSocialMedia: 'View Social Media',
        foot: 'Foot',
        contract: 'Contract',
        assistsShort: 'Assists',
        viewSocialProfile: 'View Profile'
      },
      performanceHistoryTitle: 'Performance History',
      totalCompetitions: 'total competition(s)',
      goals: 'Goals',
      assists: 'Assists',
      matches: 'Matches',
      matches90: "Matches (90')",
      season: 'Season',
      team: 'Team',
      playedMatches: 'MP',
      goalsShort: 'G',
      assistsShort: 'A',
      minutes: 'Min',
      yellowCards: 'YC',
      redCards: 'RC',
      seeLess: 'See less',
      seeAll: 'See all',
      noPerformanceData: 'No performance data per season available for this player.',
      // Roles
      roles: {
        admin: 'Administrator',
        director_deportivo: 'Sports Director',
        entrenador: 'Coach',
        segundo_entrenador: 'Assistant Coach',
        preparador_fisico: 'Physical Trainer',
        analista: 'Analyst',
        medico: 'Doctor',
        fisio: 'Physiotherapist',
        preparador: 'Trainer',
        readaptador: 'Rehabilitation Specialist',
        nutricionista: 'Nutritionist',
        jugador: 'Player',
        secretaria_tecnica: 'Technical Secretary',
        scout: 'Scout'
      },
      
      // Errors
      errors: {
        generic: 'An unexpected error occurred',
        network: 'Connection error',
        unauthorized: 'Unauthorized',
        forbidden: 'Access denied',
        notFound: 'Not found',
        validation: 'Validation error',
        server: 'Server error',
        timeout: 'Request timeout',
        offline: 'No internet connection'
      },
      
      // Sporting Direction
      sporting: {
        title: 'Sporting Direction',
        subtitle: 'Strategic management and scouting',
        playerSearch: 'Player Search',
        playerSearchIA: 'AI Player Search',
        loanedPlayers: 'Loaned Players Tracking',
        reports: 'Player Reports',
        squadPlanning: 'Squad Planning',
        coachesDatabase: 'Coaches Database',
        fieldMaps: 'Field Maps',
        expiringContracts: 'Expiring Contracts',
        marketOpportunities: 'Market Opportunities',
        similarPlayers: 'Similar Players',
        referees: 'Referees',
        searchPlaceholder: 'Search players using natural language...',
        searchExample: 'Ex: "Looking for a young striker under 23 years old playing in LaLiga"',
        noResults: 'No players found',
        searching: 'Searching players...',
        comingSoon: 'Coming Soon'
      },
      
      // Countries Explorer
      countries: {
        title: 'Nationalities Explorer',
        subtitle: 'Discover players from around the world organized by country of birth',
        backToCountries: 'Back to countries',
        totalPlayers: 'Total Players',
        avgAge: 'Average Age',
        withContract: 'With Contract',
        expiringContracts: 'Expiring Contracts',
        topPositions: 'Top Positions',
        topClubs: 'Top Clubs',
        searchPlayer: 'Search player...',
        allPositions: 'All positions',
        goalkeeper: 'Goalkeeper',
        defender: 'Defender',
        midfielder: 'Midfielder',
        forward: 'Forward',
        sortByName: 'Name',
        sortByAge: 'Age',
        loadingPlayers: 'Loading players...',
        noPlayersFound: 'No players found',
        tryDifferentFilters: 'Try different search filters',
        previousPage: 'Previous',
        nextPage: 'Next',
        page: 'Page',
        of: 'of',
        registeredPlayers: 'registered players',
        loading: 'Loading...',
        totalCountries: 'Total Countries',
        averagePerCountry: 'Average per Country',
        searchCountry: 'Search country...',
        allContinents: 'All continents',
        mostPlayers: 'Most players',
        alphabetical: 'Alphabetical',
        noCountriesFound: 'No countries found',
        includeRetired: 'Include retired',
        activePlayersOnly: 'Active only',
        clickToFilter: 'Click to filter'
      },
      
      // Video Analysis
      videoAnalysis: {
        title: 'AI Video Analysis',
        subtitle: 'Automated sports video processing',
        upload: 'Upload Video',
        processing: 'Processing',
        results: 'Results',
        uploadTab: 'Upload',
        processingTab: 'Processing',
        resultsTab: 'Results',
        noAnalysisAvailable: 'No analysis available',
        uploadVideoToStart: 'Upload a video to get started',
        downloadJson: 'Download JSON',
        downloadCsv: 'Download CSV',
        viewMetrics: 'View Metrics',
        viewSummary: 'View Summary',
        deleteAnalysis: 'Delete Analysis',
        processingStatus: 'Processing...',
        completed: 'Completed',
        failed: 'Failed',
        matchId: 'Match ID',
        videoPath: 'Video Path'
      },
      
      // Success messages
      success: {
        saved: 'Saved successfully',
        updated: 'Updated successfully',
        deleted: 'Deleted successfully',
        created: 'Created successfully',
        uploaded: 'Uploaded successfully',
        downloaded: 'Downloaded successfully',
        sent: 'Sent successfully',
        copied: 'Copied to clipboard',
        searchCompleted: 'Search completed successfully',
        playersFound: '{{count}} players found',
        registrationCompleted: 'Registration completed successfully'
      }
    }
  }
};

// Create a custom LanguageDetector with our geo detector
const customLanguageDetector = new LanguageDetector();
customLanguageDetector.addDetector(geoLanguageDetector);

i18n
  .use(customLanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // Order: geo detector first, then localStorage, then browser language
      order: ['geoLanguageDetector', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
  });

export default i18n;